import { Component, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import { NbToastrService } from '@nebular/theme';
import {
  EquipmentMaintenanceTimerConfigDto,
  EquipmentMergeDto,
  EquipmentService,
} from '@artemis-software/wr-api';
import { PendingChanges } from '@guards/pending-changes.guard';

type TimerType = EquipmentMaintenanceTimerConfigDto.TimerTypeEnum;

interface ConfigRow {
  timerType: TimerType;
  active: boolean;
  durationDays: number | null;
  reminderDurationDays: number;
  earlyToleranceDays: number;
  lateToleranceDays: number;
  imagesMandatory: boolean;
}

const TIMER_TYPES = Object.values(EquipmentMaintenanceTimerConfigDto.TimerTypeEnum);

const hasNegativeDays = (row: ConfigRow): boolean =>
  [row.reminderDurationDays, row.earlyToleranceDays, row.lateToleranceDays].some(days => (days ?? 0) < 0);

@Component({
  standalone: false,
  selector: 'wr-tga-type-maintenance-timer-configs',
  templateUrl: './tga-type-maintenance-timer-configs.component.html',
  styleUrls: ['./tga-type-maintenance-timer-configs.component.scss'],
})
export class TgaTypeMaintenanceTimerConfigsComponent implements PendingChanges {

  private readonly route = inject(ActivatedRoute);
  private readonly equipmentService = inject(EquipmentService);
  private readonly toastrService = inject(NbToastrService);

  readonly isEditing = signal(false);
  readonly saving = signal(false);
  readonly loaded = signal(false);
  readonly rows = signal<ConfigRow[]>(TIMER_TYPES.map(type => this.emptyRow(type)));
  readonly warrantyType = EquipmentMaintenanceTimerConfigDto.TimerTypeEnum.Warranty;

  tgaTypeId: string | null = null;

  constructor() {
    this.route.paramMap.pipe(takeUntilDestroyed()).subscribe(params => {
      const id = this.tgaTypeId = params.get('id');
      if (id && id !== 'new') {
        this.loadData(id);
      }
    });
  }

  loadData(id: string): void {
    this.equipmentService.findById(id).subscribe(dto => {
      const configs = dto.maintenanceTimerConfigs ?? [];
      this.rows.set(TIMER_TYPES.map(type => {
        const config = configs.find(c => c.timerType === type);
        return config ? { ...config, active: true } : this.emptyRow(type);
      }));
      this.loaded.set(true);
    });
  }

  updateRow(index: number, patch: Partial<ConfigRow>): void {
    this.rows.update(rows => rows.map((row, i) => i === index ? { ...row, ...patch } : row));
  }

  onEdit(): void {
    this.isEditing.set(true);
  }

  async cancel(): Promise<void> {
    this.isEditing.set(false);
    if (this.tgaTypeId) {
      this.loadData(this.tgaTypeId);
    }
  }

  async submit(): Promise<void> {
    if (!this.tgaTypeId) return;
    const activeRows = this.rows().filter(row => row.active);
    if (activeRows.some(row => !row.durationDays || row.durationDays < 1)) {
      this.toastrService.warning('Bitte für jede aktive Konfiguration eine Dauer von mindestens 1 Tag angeben.', 'Wartungstimer');
      return;
    }
    if (activeRows.some(hasNegativeDays)) {
      this.toastrService.warning('Erinnerung und Fristen dürfen nicht negativ sein.', 'Wartungstimer');
      return;
    }
    try {
      this.saving.set(true);
      const mergeDto: EquipmentMergeDto = {
        id: this.tgaTypeId,
        maintenanceTimerConfigs: activeRows.map(row => ({
          timerType: row.timerType,
          durationDays: row.durationDays!,
          reminderDurationDays: row.reminderDurationDays ?? 0,
          earlyToleranceDays: row.earlyToleranceDays ?? 0,
          lateToleranceDays: row.lateToleranceDays ?? 0,
          imagesMandatory: row.imagesMandatory,
        })),
      };
      await firstValueFrom(this.equipmentService.merge(mergeDto));
      this.isEditing.set(false);
      this.toastrService.success('Wartungstimer-Konfiguration gespeichert', 'Erfolg');
      this.loadData(this.tgaTypeId);
    } catch {
      /* no-op */
    } finally {
      this.saving.set(false);
    }
  }

  hasPendingChanges(): boolean {
    return this.isEditing();
  }

  private emptyRow(timerType: TimerType): ConfigRow {
    return {
      timerType,
      active: false,
      durationDays: null,
      reminderDurationDays: 0,
      earlyToleranceDays: 0,
      lateToleranceDays: 0,
      imagesMandatory: true,
    };
  }
}
